import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { financierService } from "../../../../services/financierService";
import financeApiService from "../../../../services/financeApiService";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";

/**
 * Source de vérité de la page Financeurs unifiée :
 * - la liste de tous les financeurs (peu nombreux → pas de pagination) ;
 * - `groupes` : map financeurUuid → { nom, consommation (cumul), enveloppes }
 *   pour alimenter les badges de capacité du rail et le résumé global ;
 * - les opérations CRUD financeur (création / renommage / suppression).
 *
 * Les enveloppes d'un financeur sont chargées en parallèle (Promise.all),
 * comme le dashboard de la liste des demandes.
 */
const useFinanceurs = () => {
  const [financiers, setFinanciers] = useState([]);
  const [groupes, setGroupes] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const cancelRef = useRef(false);

  const loadEnveloppes = useCallback(async (list) => {
    if (!list || list.length === 0) {
      setGroupes({});
      return;
    }
    const results = await Promise.all(
      list.map((f) =>
        financeApiService
          .listerEnveloppes(f.uuid)
          .then((res) => ({
            f,
            data: financeV2Adapters.fromApi.enveloppesList(res.data),
          }))
          .catch(() => null),
      ),
    );
    if (cancelRef.current) return;
    const map = {};
    results.filter(Boolean).forEach(({ f, data }) => {
      map[f.uuid] = {
        financeurNom: f.name || f.nom || "—",
        consommation: data.consommation,
        enveloppes: data.enveloppes,
      };
    });
    setGroupes(map);
  }, []);

  const loadFinanciers = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await financierService.getAllFinanciers();
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      if (cancelRef.current) return list;
      setFinanciers(list);
      await loadEnveloppes(list);
      return list;
    } catch (err) {
      toast.error("Erreur lors du chargement des financeurs.");
      return [];
    } finally {
      if (!cancelRef.current) setIsLoading(false);
    }
  }, [loadEnveloppes]);

  useEffect(() => {
    cancelRef.current = false;
    loadFinanciers();
    return () => {
      cancelRef.current = true;
    };
  }, [loadFinanciers]);

  // Rafraîchit uniquement les capacités (après une mutation d'enveloppe).
  const refreshEnveloppes = useCallback(
    () => loadEnveloppes(financiers),
    [financiers, loadEnveloppes],
  );

  const creer = useCallback(async (name) => {
    try {
      const res = await financierService.createFinancier({ name });
      toast.success(`${name} a été créé.`);
      return res.data?.uuid || null;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Erreur lors de la création du financeur.",
      );
      return null;
    }
  }, []);

  const renommer = useCallback(async (uuid, name) => {
    try {
      await financierService.updateFinancier({ uuid, name });
      toast.success("Financeur mis à jour.");
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Erreur lors de la mise à jour du financeur.",
      );
      return false;
    }
  }, []);

  // LS-625 — email par défaut / emails en copie du financeur, préremplis dans
  // la modale d'envoi au financeur. `name` doit être reposté (requis par
  // FinancierData) même si seul le contact change ici.
  const modifierContact = useCallback(async (uuid, name, { email, emailsCc }) => {
    try {
      await financierService.updateFinancier({ uuid, name, email, emailsCc });
      toast.success("Financeur mis à jour.");
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Erreur lors de la mise à jour du financeur.",
      );
      return false;
    }
  }, []);

  // LS-666 — active/désactive la génération de l'accord de financement (LS-657)
  // pour ce financeur. `name` doit être reposté, même raison que modifierContact.
  const modifierAccordFinancement = useCallback(
    async (uuid, name, peutGenererAccordFinancement) => {
      try {
        await financierService.updateFinancier({
          uuid,
          name,
          peutGenererAccordFinancement,
        });
        toast.success("Financeur mis à jour.");
        return true;
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la mise à jour du financeur.",
        );
        return false;
      }
    },
    [],
  );

  const supprimer = useCallback(async (uuid) => {
    try {
      await financierService.deleteFinancier(uuid);
      toast.success("Financeur supprimé.");
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Ce financeur n'a pas pu être supprimé.",
      );
      return false;
    }
  }, []);

  return {
    financiers,
    groupes,
    isLoading,
    loadFinanciers,
    refreshEnveloppes,
    creer,
    renommer,
    modifierContact,
    modifierAccordFinancement,
    supprimer,
  };
};

export default useFinanceurs;
